import { Button, TabUI, Timeline, Typography } from "matsuri-ui"
import { Link, useNavigate } from "react-router-dom"
import { css } from "@emotion/react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { ReactNode, useMemo } from "react"
import EventIcon from "@mui/icons-material/Event"
import WarningIcon from "@mui/icons-material/Warning"
import { TimelineItem } from "./SchedulePage/TimelineItem"
import { JoinedOperation } from "./SchedulePage/JoinedOperation"
import { useJoinedSchedule } from "./SchedulePage/useJoinedSchedule"
import { pickTodaySchedule } from "./SchedulePage/pickTodaySchedule"
import { RankDashboardPreview } from "./SchedulePage/RankDashboardPreview"
import { usePendingApprovalRequestWarningControl } from "./ShiftPage/usePendingApprovalRequestWarningControl"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { useListActiveOperations } from "@/hooks/useListActiveOperations"
import { useIsIncentiveTarget } from "@/hooks/useIsIncentiveTarget"
import { theme } from "@/theme"
import { LoadingMessage } from "@/components/LoadingMessage/LoadingMessage"
import { MobileMenu } from "@/templates/MobileMenu"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { CompanyNotificationBanner } from "@/components/CompanyNotifcationBanner/CompanyNotifcationBanner"
import { useTranslation } from "@/hooks/useTranslation"
import { useAuthUser } from "@/hooks/useAuthUser"
import { ScheduleMap } from "@/model/schedule"

const UnreportedScheduleAlert = ({
    unreportedScheduleCount
}: {
    unreportedScheduleCount: number
}) => {
    const { t } = useTranslation()
    return (
        <Link
            css={css`
                color: #ffffff;
                text-decoration: none;
            `}
            to="/schedule/unreported"
        >
            <div
                css={css`
                    padding: 16px 20px;
                    background-color: #fd544b;
                    border-radius: 5px;
                `}
            >
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        margin-bottom: 16px;
                    `}
                >
                    <WarningIcon />
                    <p
                        css={css`
                            margin-left: 16px;
                            font-size: 17px;
                            font-weight: 700;
                            line-height: 25px;
                            letter-spacing: 1.4px;
                        `}
                    >
                        {t(`${unreportedScheduleCount}件の清掃が未報告です！`)}
                        <br />
                        {t("報告を完了してください。")}
                    </p>
                </div>
                <div
                    css={css`
                        text-align: center;
                    `}
                >
                    <Typography variant="button">{t("報告する")}</Typography>
                </div>
            </div>
        </Link>
    )
}

const ScheduleTimeline = ({
    schedules,
    emptyMessage,
    hideDateLabel = false
}: {
    schedules: ScheduleMap<JoinedOperation>
    emptyMessage: ReactNode
    // タブ名で日付が自明な場合に、タイムラインの日付ラベルを省く
    hideDateLabel?: boolean
}) => {
    const { t } = useTranslation()

    return (
        <div
            css={css`
                padding-top: 24px;
            `}
        >
            {Object.keys(schedules).length === 0 ? (
                <EmptyState>{emptyMessage}</EmptyState>
            ) : (
                <Timeline
                    dates={schedules}
                    renderItem={(props: JoinedOperation) =>
                        TimelineItem({ ...props, t })
                    }
                    renderLabel={hideDateLabel ? () => <></> : undefined}
                />
            )}
        </div>
    )
}

export const SchedulePage = () => {
    const navigate = useNavigate()
    const { token } = useAuth()
    const { t } = useTranslation()
    const { userId } = useAuthUser(token)
    const { data: incentiveData } = useIsIncentiveTarget()
    const isTarget = incentiveData?.isTarget

    const {
        data: { operations } = { operations: undefined },
        isLoading: isLoadingOperations
    } = useListActiveOperations(token)

    const preparedOperations = useMemo(
        () => operations?.filter(op => op.cleaningStatus === "prepared") ?? [],
        [operations]
    )

    const unreportedScheduleCount = useMemo(() => {
        return (
            operations?.filter(op => op.cleaningStatus !== "prepared") ?? []
        ).length
    }, [operations])

    const {
        joinedSchedules: joinedPreparedSchedules,
        isLoading: isScheduleLoading
    } = useJoinedSchedule(token, preparedOperations)

    const loading = isLoadingOperations || isScheduleLoading

    // 清掃予定日より前の作業に入ってしまうのを防ぐため、デフォルトでは本日分だけを見せる。
    // 日付を跨いだ後も再レンダリングのたびに本日分を取り直せるよう、メモ化しない
    const todaySchedules = pickTodaySchedule(joinedPreparedSchedules)

    usePendingApprovalRequestWarningControl()

    return (
        <>
            <div
                css={css`
                    /* 清掃が少ない時に背景色が画面の途中で切れて見えないようにする */
                    /* lvh未対応の環境向けにvhをフォールバックとして先に指定する */
                    min-height: 100vh;
                    min-height: 100lvh;
                    padding-top: env(safe-area-inset-top, 0px);
                    background-color: ${
                        theme.palette.intentions.cleanerBackground.main
                    };
                `}
            >
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        margin-right: 16px;
                    `}
                >
                    <MobileMenu />
                </div>
                <CompanyNotificationBanner />
                {isTarget && (
                    <RankDashboardPreview
                        workerId={userId}
                        onClick={() => navigate("/rank-dashboard")}
                    />
                )}
                <MobileAppHeader title={<h3>{t("清掃予定")}</h3>}>
                    <div
                        css={css`
                            padding: 16px 16px 98px 16px; // 下部はスケジュールアイコンに被らない高さ 34px+アイコンボタンの高さ+余白
                        `}
                    >
                        {unreportedScheduleCount !== 0 && (
                            <UnreportedScheduleAlert
                                unreportedScheduleCount={
                                    unreportedScheduleCount
                                }
                            />
                        )}

                        <div
                            css={css`
                                margin-top: 24px;
                            `}
                        >
                            {loading ? (
                                <LoadingMessage />
                            ) : (
                                <TabUI
                                    tabPosition="evenly"
                                    tabs={[
                                        {
                                            title: t("本日"),
                                            screen: (
                                                <ScheduleTimeline
                                                    schedules={todaySchedules}
                                                    emptyMessage={
                                                        <>
                                                            {t(
                                                                "本日の未清掃はありません！"
                                                            )}
                                                            <br />
                                                            <span
                                                                css={css`
                                                                    font-size: 0.875em;
                                                                    font-weight: normal;
                                                                `}
                                                            >
                                                                {t(
                                                                    "明日以降の予定は「すべて」にあります"
                                                                )}
                                                            </span>
                                                        </>
                                                    }
                                                    hideDateLabel
                                                />
                                            )
                                        },
                                        {
                                            title: t("すべて"),
                                            screen: (
                                                <ScheduleTimeline
                                                    schedules={
                                                        joinedPreparedSchedules
                                                    }
                                                    emptyMessage={t(
                                                        "現在未清掃はありません！"
                                                    )}
                                                />
                                            )
                                        }
                                    ]}
                                />
                            )}
                        </div>
                    </div>
                </MobileAppHeader>

                <div
                    css={css`
                        position: fixed;
                        bottom: 34px;
                        left: 16px;
                    `}
                >
                    <Button
                        as={Link}
                        to="/shift"
                        icon={<EventIcon />}
                        rounded
                        variant="filled"
                        size="large"
                        css={css`
                            z-index: ${theme.zIndex.speedDial};
                            box-shadow: ${theme.shadows[5]};
                        `}
                    />
                </div>
            </div>
        </>
    )
}
